import type { PriceDaily, RMR } from "epsm-api-types/models";
import { memo, useRef } from "react";
import {
	formatValue,
	formatValueCurrency,
} from "@/components/widgets/helpers/FormattingHelpers";
import { DEFAULT_COLOR, SECTORS, TIME_RANGE_MAP } from "../../../config";
import { useCreateSingleSectorChart } from "../../../lib/hooks/useCreateSingleSectorChart";
import type {
	SectorsChartType,
	SectorsTimeRange,
	SectorsType,
} from "../../../types";

interface OneSectorChartProps {
	tickerData: PriceDaily[] | RMR[];
	timeRange: SectorsTimeRange;
	chartType: SectorsChartType;
}

// Type guards to check data types
const isPriceDailyArray = (
	data: PriceDaily[] | RMR[],
): data is PriceDaily[] => {
	return data.length === 0 || "adj_close" in data[0] || "close" in data[0];
};

const isRMRArray = (data: PriceDaily[] | RMR[]): data is RMR[] => {
	return data.length === 0 || "index_ratio" in data[0];
};

export const OneSectorChart: React.FC<OneSectorChartProps> = memo(
	({ tickerData, timeRange, chartType }) => {
		const chartRef = useRef<HTMLDivElement>(null);
		const ticker = useCreateSingleSectorChart(
			chartRef,
			tickerData,
			timeRange,
			chartType,
		) as SectorsType;

		const closeField = TIME_RANGE_MAP[timeRange]?.closePrice || "close";

		// Type-safe functions that check data type before accessing properties
		const formatPriceValue = (data: PriceDaily[] | RMR[]): string => {
			if (chartType === "Price" && isPriceDailyArray(data)) {
				return data.length > 0
					? formatValueCurrency(
							data[data.length - 1][closeField] ??
								data[data.length - 1].adj_close,
							"N/A",
						)
					: "N/A";
			}
			return "N/A";
		};

		const formatRatioValue = (data: PriceDaily[] | RMR[]): string => {
			if (chartType === "Ratio" && isRMRArray(data)) {
				return data.length > 0
					? formatValue(data[data.length - 1].index_ratio, "num", {}, "N/A")
					: "N/A";
			}
			return "N/A";
		};

		const mapLastValue: Record<
			SectorsChartType,
			(data: PriceDaily[] | RMR[]) => string
		> = {
			Price: formatPriceValue,
			Ratio: formatRatioValue,
		};

		return (
			<div className="w-full max-w-[120px] flex flex-col justify-between items-center">
				{ticker === null ? (
					<p data-testid="error-message">Invalid ticker</p>
				) : (
					<div
						data-testid="one-sector-chart"
						className="flex flex-col justify-between items-center h-full w-full min-w-[80px]"
					>
						<p
							className="font-bold flex items-top"
							style={{
								color: SECTORS[ticker].color || DEFAULT_COLOR,
							}}
						>
							{`${ticker}:`} <br />
							{`${SECTORS[ticker].name} `}
						</p>

						<div
							ref={chartRef}
							style={{ width: "100%", height: "100px" }}
							className="mb-4"
						/>

						<p
							className="font-bold "
							style={{
								color: SECTORS[ticker].color || DEFAULT_COLOR,
							}}
						>
							{mapLastValue[chartType](tickerData)}
						</p>
					</div>
				)}
			</div>
		);
	},
);

OneSectorChart.displayName = "OneSectorChart";
